import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError } from "../lib/api.js";
import { X, Plus, Trash2, Paperclip } from "lucide-react";

const SOURCES = ["Local stock", "Factory", "Supplier", "Backorder"];
const ACCEPTED_TYPES = ".pdf,.jpg,.jpeg,.png,.xlsx";
const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB

export default function NewPartsRequestDialog({ presetCaseId = null, onClose, onCreated }) {
  const navigate = useNavigate();
  const [enums, setEnums] = useState(null);
  const [cases, setCases] = useState([]);
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [prefillInfo, setPrefillInfo] = useState(null); // { pcp_number, pcp_id, inherited_from }

  const [form, setForm] = useState({
    service_case_id: presetCaseId || "",
    responsible_parts_user_id: "",
    priority: "Normal",
    notes: "",
    pcp_id: null,
  });

  const [lines, setLines] = useState([
    { part_number: "", description: "", quantity_ordered: 1, source: "Factory", line_status: "Requested", eta: "", notes: "" },
  ]);
  const [quotationFile, setQuotationFile] = useState(null);
  const [inheritedQuotationId, setInheritedQuotationId] = useState(null);

  useEffect(() => {
    api.get("/enums").then((r) => setEnums(r.data));
    api.get("/users").then((r) => setUsers(r.data.filter((u) => !u.deactivated)));
    if (!presetCaseId) {
      api.get("/service-cases?open_only=true").then((r) => setCases(r.data));
    }
  }, [presetCaseId]);

  // Pré-preenchimento a partir do PCP encerrado da SOM. Quando o SA passa a
  // SOM para "Aprovado" e abre este diálogo, as linhas do orçamento classificado
  // + o anexo da cotação são copiados automaticamente. Reduz muito o
  // double-entry que antes era manual.
  useEffect(() => {
    if (!presetCaseId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: sc } = await api.get(`/service-cases/${presetCaseId}`);
        const linkedPcps = sc.linked_pcps || [];
        if (linkedPcps.length === 0) return;
        // Preferir Encerrado (orçamento pronto); senão o mais recente.
        const closed = linkedPcps.filter((p) => p.status === "Encerrado");
        const pick = (closed[closed.length - 1]) || linkedPcps[linkedPcps.length - 1];
        if (!pick) return;
        const { data: pcp } = await api.get(`/classification-requests/${pick.id}`);
        const parts = (pcp.classified_parts || []).filter(
          (p) => p && (p.part_number || p.description),
        );
        if (parts.length > 0) {
          const mapped = parts.map((p) => ({
            part_number: (p.part_number || "").trim(),
            description: (p.description || "").trim(),
            quantity_ordered: Number(p.quantity) || 1,
            source: p.source || "Factory",
            line_status: "Requested",
            eta: "",
            notes: p.notes || "",
          }));
          if (!cancelled) setLines(mapped);
        }
        if (!cancelled) {
          setForm((f) => ({ ...f, pcp_id: pcp.id }));
          setInheritedQuotationId(pcp.alidata_quotation_attachment_id || null);
          setPrefillInfo({
            pcp_number: pcp.number,
            pcp_id: pcp.id,
            parts_count: parts.length,
            has_quote: !!pcp.alidata_quotation_attachment_id,
          });
        }
      } catch {
        // silent — pré-preenchimento é um nice-to-have, não bloqueia a criação manual
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [presetCaseId]);

  const partsUsers = useMemo(
    () => users.filter((u) => ["parts_user", "admin", "national_aftersales_manager", "department_manager"].includes(u.role)),
    [users]
  );

  const updateLine = (idx, patch) => {
    setLines((ls) => ls.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };
  const addLine = () => {
    setLines((ls) => [...ls, { part_number: "", description: "", quantity_ordered: 1, source: "Factory", line_status: "Requested", eta: "", notes: "" }]);
  };
  const removeLine = (idx) => {
    setLines((ls) => (ls.length > 1 ? ls.filter((_, i) => i !== idx) : ls));
  };

  const hasValidLines =
    lines.length > 0 &&
    lines.every((l) => l.part_number.trim() && l.description.trim() && Number(l.quantity_ordered) > 0);
  const hasAttachment = !!quotationFile || !!inheritedQuotationId;
  const canSubmit =
    form.service_case_id &&
    form.responsible_parts_user_id &&
    (hasValidLines || hasAttachment);

  const submit = async () => {
    setErr("");
    if (!canSubmit) {
      setErr("Fill in the service case, responsible person and at least one valid line.");
      return;
    }
    setBusy(true);
    try {
      // Optional upload — do this first so the returned attachment_id can be
      // included in the PEC create payload. The attachment is temporarily
      // parented to the SOM and re-parented server-side once the PEC exists.
      let quotation_attachment_id = null;
      if (quotationFile) {
        const fd = new FormData();
        fd.append("file", quotationFile);
        fd.append("record_type", "service_case");
        fd.append("record_id", form.service_case_id);
        const up = await api.post("/attachments/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        quotation_attachment_id = up.data.id;
      }
      const payload = {
        ...form,
        pcp_id: form.pcp_id || null,
        quotation_attachment_id: quotation_attachment_id || inheritedQuotationId || null,
        lines: hasValidLines
          ? lines.map((l) => ({
              part_number: l.part_number.trim(),
              description: l.description.trim(),
              quantity_ordered: Number(l.quantity_ordered),
              source: l.source,
              line_status: l.line_status,
              eta: l.eta || null,
              notes: l.notes || null,
            }))
          : [],
      };
      const { data } = await api.post("/parts-requests", payload);
      if (onCreated) onCreated(data);
      else navigate(`/parts-requests/${data.id}`);
    } catch (e) {
      setErr(formatError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-md shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="new-pr-dialog">
        <div className="sticky top-0 bg-white px-5 py-3 border-b border-gray-200 flex items-center justify-between z-10">
          <h2 className="font-heading text-base font-bold">New parts request</h2>
          <button onClick={onClose} className="text-[#6C757D] hover:text-[#1A1A2E]" aria-label="Close"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 space-y-4">
          {prefillInfo && (
            <div
              data-testid="new-pr-prefill-banner"
              className="rounded-sm border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900 flex items-start gap-2"
            >
              <span aria-hidden="true" className="mt-0.5">✨</span>
              <span>
                Pré-preenchido a partir do PCP <b>{prefillInfo.pcp_number}</b>:{" "}
                {prefillInfo.parts_count > 0 && (
                  <>
                    <b>{prefillInfo.parts_count}</b> linha{prefillInfo.parts_count === 1 ? "" : "s"} de peças
                  </>
                )}
                {prefillInfo.parts_count > 0 && prefillInfo.has_quote && " · "}
                {prefillInfo.has_quote && <>orçamento do classificador anexado</>}
                . Podes editar antes de criar.
              </span>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            {!presetCaseId && (
              <label className="block col-span-2">
                <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">Service case *</span>
                <select
                  value={form.service_case_id}
                  onChange={(e) => setForm({ ...form, service_case_id: e.target.value })}
                  className={inputCls}
                  data-testid="new-pr-case"
                >
                  <option value="">Select…</option>
                  {cases.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.number} · {c.customer_name} · {c.brand} {c.vehicle_registration}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">Parts responsible *</span>
              <select
                value={form.responsible_parts_user_id}
                onChange={(e) => setForm({ ...form, responsible_parts_user_id: e.target.value })}
                className={inputCls}
                data-testid="new-pr-responsible"
              >
                <option value="">Select…</option>
                {partsUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name} ({u.role.replaceAll("_", " ")})
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">Priority</span>
              <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className={inputCls} data-testid="new-pr-priority">
                {(enums?.priorities || ["Low", "Normal", "High", "Critical"]).map((p) => <option key={p}>{p}</option>)}
              </select>
            </label>
          </div>

          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">Notes</span>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className={inputCls} />
          </label>

          {/* Optional supplier quotation attachment */}
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">Anexar orçamento (opcional)</span>
            {!quotationFile ? (
              <div className="mt-0.5 flex items-center gap-2 border border-dashed border-gray-300 rounded-sm px-3 py-2 hover:border-[#1F3864]">
                <Paperclip className="w-3.5 h-3.5 text-[#6C757D]" />
                <input
                  type="file"
                  accept={ACCEPTED_TYPES}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    if (f.size > MAX_FILE_BYTES) {
                      setErr("Ficheiro demasiado grande. Máximo 10MB.");
                      e.target.value = "";
                      return;
                    }
                    setErr("");
                    setQuotationFile(f);
                  }}
                  className="text-xs text-[#1A1A2E] file:mr-3 file:px-2 file:py-1 file:border-0 file:bg-[#F4F6F8] file:text-[#1F3864] file:text-xs file:rounded-sm hover:file:bg-[#E7EBF1] cursor-pointer"
                  data-testid="new-pr-quotation-input"
                />
                <span className="text-[10px] text-[#6C757D] ml-auto">PDF, JPG, PNG, XLSX · máx. 10MB</span>
              </div>
            ) : (
              <div className="mt-0.5 flex items-center gap-2 bg-[#E8F5E9] border border-[#28A745]/40 rounded-sm px-3 py-2 text-xs text-[#0F5132]" data-testid="new-pr-quotation-selected">
                <Paperclip className="w-3.5 h-3.5 shrink-0" />
                <span className="font-medium truncate">{quotationFile.name}</span>
                <span className="text-[10px] text-[#6C757D] font-mono ml-1">
                  {(quotationFile.size / 1024).toFixed(0)} KB
                </span>
                <button
                  type="button"
                  onClick={() => setQuotationFile(null)}
                  className="ml-auto p-0.5 hover:bg-[#28A745]/20 rounded"
                  aria-label="Remover ficheiro"
                  data-testid="new-pr-quotation-remove"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </label>

          {/* Lines */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-widest text-[#6C757D]">Parts lines (at least one, or attach the quotation above)</div>
              <button onClick={addLine} className="text-xs text-[#1F3864] hover:underline inline-flex items-center gap-1" data-testid="new-pr-add-line">
                <Plus className="w-3 h-3" /> Add line
              </button>
            </div>
            <div className="space-y-2">
              {lines.map((l, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end bg-[#F8FAFC] p-2 rounded-sm" data-testid={`new-pr-line-${i}`}>
                  <input
                    placeholder="Part #"
                    value={l.part_number}
                    onChange={(e) => updateLine(i, { part_number: e.target.value })}
                    className={`${inputCls} col-span-2 font-mono text-xs`}
                    data-testid={`new-pr-line-${i}-pn`}
                  />
                  <input
                    placeholder="Description"
                    value={l.description}
                    onChange={(e) => updateLine(i, { description: e.target.value })}
                    className={`${inputCls} col-span-4`}
                    data-testid={`new-pr-line-${i}-desc`}
                  />
                  <input
                    type="number"
                    min="1"
                    value={l.quantity_ordered}
                    onChange={(e) => updateLine(i, { quantity_ordered: e.target.value })}
                    className={`${inputCls} col-span-1`}
                    data-testid={`new-pr-line-${i}-qty`}
                  />
                  <select value={l.source} onChange={(e) => updateLine(i, { source: e.target.value })} className={`${inputCls} col-span-2`} data-testid={`new-pr-line-${i}-source`}>
                    {SOURCES.map((s) => <option key={s}>{s}</option>)}
                  </select>
                  <input
                    type="date"
                    value={l.eta}
                    onChange={(e) => updateLine(i, { eta: e.target.value })}
                    className={`${inputCls} col-span-2`}
                    placeholder="ETA"
                    data-testid={`new-pr-line-${i}-eta`}
                  />
                  <button
                    onClick={() => removeLine(i)}
                    className="col-span-1 text-[#DC3545] hover:text-[#a82733] p-1.5"
                    aria-label="Remove line"
                    disabled={lines.length === 1}
                    data-testid={`new-pr-line-${i}-remove`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {err && <div className="px-3 py-2 bg-[#F8D7DA] text-[#721C24] border border-[#DC3545]/30 rounded-sm text-sm" data-testid="new-pr-error">{err}</div>}
        </div>

        <div className="sticky bottom-0 bg-white px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-sm hover:bg-[#F4F6F8]">Cancel</button>
          <button
            onClick={submit}
            disabled={busy || !canSubmit}
            className="px-4 py-2 text-sm bg-[#1F3864] hover:bg-[#142542] text-white rounded-sm disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="new-pr-submit"
          >
            {busy ? "Creating…" : "Create parts request"}
          </button>
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3864]/30";
